import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, setAuthFailureHandler } from '../services/api';

const TOKEN_KEY = 'tb_token';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken]       = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser]         = useState(null);
  const [checking, setChecking] = useState(() => !!localStorage.getItem(TOKEN_KEY));

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  // Any 401 from the API clears the session
  useEffect(() => { setAuthFailureHandler(logout); }, [logout]);

  // Validate a stored token on startup
  useEffect(() => {
    if (!token) { setChecking(false); return; }
    let cancelled = false;
    authApi.me()
      .then(r => { if (!cancelled) setUser(r.data); })
      .catch(() => { if (!cancelled) logout(); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
    // Intentionally run only on mount to validate a stored token.
    // eslint-disable-next-line
  }, []);

  const applyAuth = ({ token: newToken, userId, email }) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    setUser({ id: userId, email });
  };

  // Refresh the cached user (e.g. after toggling MFA in settings)
  const refreshUser = async () => { setUser((await authApi.me()).data); };

  // applyAuth sets {id, email} from the auth response; hydrate the full record
  // (mfaEnabled, admin, …) so the UI doesn't wait for the next page load.
  const hydrate = async () => { try { await refreshUser(); } catch { /* keep basic user */ } };

  // Returns { mfaRequired, mfaToken }. When MFA is off the session is applied here
  // and mfaRequired is false; when on, the caller must call completeMfa next.
  const login = async (email, password) => {
    const { data } = await authApi.login({ email, password });
    if (data.mfaRequired) return { mfaRequired: true, mfaToken: data.mfaToken };
    applyAuth(data);
    await hydrate();
    return { mfaRequired: false };
  };

  const completeMfa = async (mfaToken, code) => {
    applyAuth((await authApi.mfaVerify({ mfaToken, code })).data);
    await hydrate();
  };

  // Registering no longer starts a session — the account must be verified by
  // email first. Returns the backend's {verificationRequired, email} response.
  const register = async (email, password) => {
    const { data } = await authApi.register({ email, password });
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, token, checking, login, completeMfa, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
